import React, { useMemo } from "react";
import { Pill } from "@truevault/aquilo";
import clsx from "clsx";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { OrganizationPublicId } from "../../../../common/service/server/Types";
import { DataMapPatchMutation, useDataMap } from "../../../../common/hooks/api/useDataMap";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import { DataMapDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import {
  useCollectionGroups,
  useEmploymentCollectionGroups,
  useNonEmploymentCollectionGroups,
} from "../../../hooks/useCollectionGroups";
import { useDisclosuresForCollectionGroups } from "../../../hooks/useDisclosuresForCollectionGroups";
import {
  isDataRecipientDisclosureEnabled,
  useVendorDisclosureCardControls,
  VendorDisclosureAssociationToggle,
  VendorDisclosurePICGroups,
} from "../data-flows/DataFlowsDisclosuresPage";

const ROLE_DEFINITIONS = {
  recipient: {
    term: "Data Recipient",
    definition:
      "a party to whom your business gives access to personal data, such as a service provider.",
  },
  source: {
    term: "Data Source",
    definition: "a party from which your business collects personal data.",
  },
} as const;

/** The one-line "Data Recipient = …" / "Data Source = …" definition under a role heading. */
export function VendorRoleDefinition({
  role,
  className,
}: {
  role: keyof typeof ROLE_DEFINITIONS;
  className?: string;
}) {
  const { term, definition } = ROLE_DEFINITIONS[role];
  return (
    <div className={clsx("text-[14px] leading-[20px] text-neutral-500", className)}>
      <span className="font-medium">{term}</span> = {definition}
    </div>
  );
}

/** Link-styled role removal ("Remove as recipient" / "Remove as source") beside a role heading. */
export function VendorRoleRemoveLink({
  children,
  onClick,
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="shrink-0 text-[14px] leading-[24px] font-medium text-primary-600 underline cursor-pointer bg-transparent border-0 p-0 hover:text-primary-700 disabled:cursor-not-allowed disabled:text-neutral-400"
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * "X is a Recipient of the following data:" with the role definition, then one disclosure card per
 * consumer (or, for HR, employment) collection group. Shared by the vendor settings modal's Data
 * Disclosures tab and the prompt that follows adding a source that isn't in the Vendor List yet.
 */
export function VendorRecipientDisclosures({
  vendor,
  org,
  collectionContext = "CONSUMER",
  headerAction,
}: {
  vendor: OrganizationDataRecipientDto;
  org: OrganizationDto;
  collectionContext?: CollectionContext;
  /** Rendered at the right of the heading, e.g. a "Remove as recipient" link. */
  headerAction?: React.ReactNode;
}) {
  const [consumerGroups, consumerGroupsRequest] = useCollectionGroups(org.id, [
    "BUSINESS_TO_CONSUMER",
  ]);
  const [employmentGroups, employmentGroupsRequest] = useEmploymentCollectionGroups(org.id);
  const isHr = collectionContext === "EMPLOYEE";
  const groups = isHr ? employmentGroups : consumerGroups;
  const groupsRequest = isHr ? employmentGroupsRequest : consumerGroupsRequest;
  const {
    data: dataMap,
    request: dataMapRequest,
    mutators: { patch: patchDataMap },
  } = useDataMap(org.id);

  if (!groupsRequest.success || !dataMapRequest.success) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[12px]">
      <div>
        <div className="flex items-start justify-between gap-[12px]">
          <div className="flex flex-wrap items-center gap-x-[8px] text-[16px] leading-[24px] font-semibold text-neutral-700">
            <span>{vendor.name} is a</span>
            <Pill variant="completed" noMargin>
              Recipient
            </Pill>
            <span>of the following data:</span>
          </div>
          {headerAction}
        </div>
        <VendorRoleDefinition role="recipient" className="mt-[4px]" />
      </div>

      {groups.map((group) => (
        <VendorRecipientDisclosureCard
          key={group.id}
          vendor={vendor}
          orgId={org.id}
          group={group}
          collectionContext={collectionContext}
          dataMap={dataMap}
          patchDataMap={patchDataMap}
        />
      ))}
    </div>
  );
}

function VendorRecipientDisclosureCard({
  vendor,
  orgId,
  group,
  collectionContext,
  dataMap,
  patchDataMap,
}: {
  vendor: OrganizationDataRecipientDto;
  orgId: OrganizationPublicId;
  group: CollectionGroupDetailsDto;
  collectionContext: CollectionContext;
  dataMap: DataMapDto;
  patchDataMap: DataMapPatchMutation;
}) {
  const groups = useMemo(() => [group], [group]);
  const contexts = useMemo(() => [collectionContext], [collectionContext]);
  const disclosureData = useDisclosuresForCollectionGroups(
    orgId,
    groups,
    contexts,
    (dataRecipient) => dataRecipient.vendorId === vendor.vendorId,
  );
  const disclosureEnabled = useMemo(() => {
    return isDataRecipientDisclosureEnabled(dataMap, vendor, group);
  }, [dataMap, group, vendor]);
  // A consumer card's relevant group count is the non-employment one; HR's the employment one.
  const [nonEmploymentGroups] = useNonEmploymentCollectionGroups(orgId);
  const [employmentGroups] = useEmploymentCollectionGroups(orgId);
  const contextGroups = collectionContext === "EMPLOYEE" ? employmentGroups : nonEmploymentGroups;
  const hasMultipleGroupsInContext = contextGroups.length > 1;
  const {
    selectedPicGroupIds,
    pendingPicGroupIds,
    recommendedGroups,
    handlePICGroupDisclosureChange,
    collectionGroup,
    toggleDisabled,
    controlsDisabled,
    handleDisclosureToggleChange,
  } = useVendorDisclosureCardControls({
    dataRecipient: vendor,
    groups,
    disclosureData,
    dataMap,
    patchDataMap,
  });

  if (
    !disclosureData.requests.dataMapRequest.success ||
    !disclosureData.requests.recipientsRequest.success
  ) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-400">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[12px]">
      {/* With one collection group there is nothing to choose between, so an attached vendor shows
          no toggle; an unattached one keeps it as the only way to attach. */}
      {(hasMultipleGroupsInContext || !disclosureEnabled) && (
        <VendorDisclosureAssociationToggle
          dataRecipient={vendor}
          collectionGroup={collectionGroup}
          disclosureEnabled={disclosureEnabled}
          disabled={toggleDisabled}
          onChange={handleDisclosureToggleChange}
          rowClassName="flex items-center gap-[12px]"
          labelClassName="text-[16px] leading-[24px] font-semibold text-neutral-700"
          labelContent={
            <>
              Collects data about <span className="text-primary-700">{collectionGroup?.name}</span>
            </>
          }
        />
      )}

      {disclosureEnabled && (
        <div
          className={clsx(
            "rounded-[10px] border border-neutral-300 bg-white p-[24px]",
            controlsDisabled && "pointer-events-none opacity-60",
          )}
        >
          <VendorDisclosurePICGroups
            dataMap={dataMap}
            picGroups={disclosureData.collectedPICGroups}
            dataRecipient={vendor}
            groups={groups}
            selectedPicGroupIds={selectedPicGroupIds}
            pendingPicGroupIds={pendingPicGroupIds}
            recommendedGroups={recommendedGroups}
            onChange={handlePICGroupDisclosureChange}
            disabled={controlsDisabled}
            className="space-y-[4px]"
          />
        </div>
      )}
    </div>
  );
}
